import { SketchPicker } from 'react-color';
import React, { useState, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Button, Dropdown, FormControl } from 'react-bootstrap';
import { Stage, Layer, Image as KonvaImage, Text as KonvaText } from 'react-konva';
import Konva from 'konva';

const EFFECTS = ['none', 'Grayscale', 'Sepia', 'Invert', 'Blur'];
const FONTS = ['Arial', 'Times New Roman', 'Comic Sans MS', 'Roboto', 'Impact'];
const MAX_ITEMS = 20;
const STAGE_WIDTH = 400;
const STAGE_HEIGHT = 600;

const ReelEditor = ({ media, onSave, onCancel, canvasWidth, canvasHeight }) => {
    const stageWidth = STAGE_WIDTH;
    const stageHeight = STAGE_HEIGHT;

    const [image, setImage] = useState(null);
    const [isGif, setIsGif] = useState(false);
    const [imageProps, setImageProps] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [texts, setTexts] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [effect, setEffect] = useState('none');
    const stageRef = useRef(null);

    useEffect(() => {
        if (!media) return;
        const img = new window.Image();
        img.src = URL.createObjectURL(media);
        img.onload = () => {
            setImage(img);
            setIsGif(media.type === 'image/gif');

            const stageWidth = STAGE_WIDTH;
            const stageHeight = STAGE_HEIGHT;

            const imgWidth = img.width;
            const imgHeight = img.height;

            const scale = Math.max(stageWidth / imgWidth, stageHeight / imgHeight);
            const newWidth = imgWidth * scale;
            const newHeight = imgHeight * scale;

            setImageProps({
                x: (stageWidth - newWidth) / 2,
                y: (stageHeight - newHeight) / 2,
                width: newWidth,
                height: newHeight,
                draggable: true,
                dragBoundFunc: (pos) => {
                    const minX = stageWidth - newWidth;
                    const minY = stageHeight - newHeight;
                    return {
                        x: Math.max(Math.min(0, pos.x), minX),
                        y: Math.max(Math.min(0, pos.y), minY),
                    };
                }
            });
        };
    }, [media]);

    const addText = () => {
        if (texts.length >= MAX_ITEMS) {
            Swal.fire('Limit dosažen', 'Můžeš přidat maximálně 20 prvků.', 'warning');
            return;
        }
        const newText = {
            id: Date.now(),
            text: 'Nový text',
            x: 50,
            y: 50,
            fontSize: 30,
            fill: '#ffffff',
            fontFamily: 'Arial',
            draggable: true,
        };
        setTexts([...texts, newText]);
    };

    const addEmoji = (emoji) => {
        if (texts.length >= MAX_ITEMS) {
            Swal.fire('Limit dosažen', 'Můžeš přidat maximálně 20 prvků.', 'warning');
            return;
        }
        const newEmoji = {
            id: Date.now(),
            text: emoji,
            x: 50,
            y: 50,
            fontSize: 40,
            fill: '#ffffff',
            fontFamily: 'Arial',
            draggable: true,
        };
        setTexts([...texts, newEmoji]);
    };

    const handleExport = () => {
        const uri = stageRef.current.toDataURL();
        fetch(uri)
            .then(res => res.blob())
            .then(blob => {
                const file = new File([blob], 'edited-reel.png', { type: 'image/png' });
                onSave(file);
            });
    };

    const handleTextClick = (id, currentText) => {
        const selected = texts.find(t => t.id === id);
        setSelectedId(id);
        setEditingText(currentText);
    };

    const handleTextChange = (e) => {
        setEditingText(e.target.value);
        setTexts(texts.map((t) =>
            t.id === selectedId ? { ...t, text: e.target.value } : t
        ));
    };

    const handleColorChange = (color) => {
        setTexts(texts.map((t) =>
            t.id === selectedId ? { ...t, fill: color } : t
        ));
    };

    const handleFontChange = (font) => {
        setTexts(texts.map((t) =>
            t.id === selectedId ? { ...t, fontFamily: font } : t
        ));
    };

    const getFilters = () => {
        if (effect === 'Grayscale') return [Konva.Filters.Grayscale];
        if (effect === 'Sepia') return [Konva.Filters.Sepia];
        if (effect === 'Invert') return [Konva.Filters.Invert];
        if (effect === 'Blur') return [Konva.Filters.Blur];
        return [];
    };

    return (
        <div>
            {!isGif && (
                <div className="d-flex justify-content-between mb-2">
                    <div>
                        <Button size="sm" variant="secondary" onClick={addText} className="me-2">
                            Přidat text
                        </Button>
                        <Dropdown onSelect={addEmoji} className="d-inline me-2">
                            <Dropdown.Toggle size="sm" variant="secondary">😊 Emoji</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item eventKey="🔥">🔥</Dropdown.Item>
                                <Dropdown.Item eventKey="🎉">🎉</Dropdown.Item>
                                <Dropdown.Item eventKey="❤️">❤️</Dropdown.Item>
                                <Dropdown.Item eventKey="😂">😂</Dropdown.Item>
                                <Dropdown.Item eventKey="⭐">⭐</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown onSelect={(e) => setEffect(e)} className="d-inline me-2">
                            <Dropdown.Toggle size="sm" variant="secondary">🎨 Efekt (neaktivní)</Dropdown.Toggle>
                            <Dropdown.Menu>
                                {EFFECTS.map(eff => (
                                    <Dropdown.Item key={eff} eventKey={eff}>{eff}</Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                        {selectedId && (
                            <Dropdown onSelect={handleFontChange} className="d-inline">
                                <Dropdown.Toggle size="sm" variant="secondary">🖋️ Font</Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {FONTS.map((font) => (
                                        <Dropdown.Item key={font} eventKey={font}>{font}</Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        )}
                    </div>
                </div>
            )}

            {selectedId && !isGif && (
                <div className="d-flex align-items-center mb-2">
                    <FormControl
                        value={editingText}
                        onChange={handleTextChange}
                        placeholder="Uprav text zde..."
                        className="me-3"
                    />
                    <Dropdown onSelect={handleFontChange} className="me-3">
                        <Dropdown.Toggle size="sm" variant="secondary">🖋️ Font</Dropdown.Toggle>
                        <Dropdown.Menu>
                            {FONTS.map((font) => (
                                <Dropdown.Item key={font} eventKey={font}>{font}</Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                    <div>
                        <Dropdown>
                            <Dropdown.Toggle size="sm" variant="secondary">🎨 Barva</Dropdown.Toggle>
                            <Dropdown.Menu style={{ padding: '10px' }}>
                                <SketchPicker
                                    color={texts.find(t => t.id === selectedId)?.fill || '#ffffff'}
                                    onChangeComplete={(c) => handleColorChange(c.hex)}
                                    disableAlpha
                                />
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
            )}


            <Stage width={STAGE_WIDTH} height={STAGE_HEIGHT} ref={stageRef} style={{ border: '1px solid #ccc', margin: '0 auto' }}>
                <Layer>
                    {image && (
                        <KonvaImage
                            image={image}
                            x={imageProps.x}
                            y={imageProps.y}
                            width={imageProps.width}
                            height={imageProps.height}
                            draggable
                            dragBoundFunc={imageProps.dragBoundFunc}
                            onDragEnd={(e) => {
                                setImageProps((prev) => ({
                                    ...prev,
                                    x: e.target.x(),
                                    y: e.target.y(),
                                }));
                            }}
                            filters={getFilters()}
                            blurRadius={effect === 'Blur' ? 10 : 0}
                        />
                    )}
                    {!isGif && texts.map((t, idx) => (
                        <KonvaText
                            key={t.id}
                            {...t}
                            onClick={() => handleTextClick(t.id, t.text)}
                            onTap={() => handleTextClick(t.id, t.text)}
                            onDragEnd={(e) => {
                                const newTexts = [...texts];
                                newTexts[idx] = {
                                    ...newTexts[idx],
                                    x: e.target.x(),
                                    y: e.target.y(),
                                };
                                setTexts(newTexts);
                            }}
                            draggable
                        />
                    ))}
                </Layer>
            </Stage>

            <div className="d-flex justify-content-end mt-3">
                <Button variant="secondary" className="me-2" onClick={onCancel}>Zrušit</Button>
                <Button variant="success" onClick={handleExport}>Uložit změny</Button>
            </div>
        </div>
    );
};

export default ReelEditor;